import React from 'react';

import {
	AGENT_EDIT_CHROME_DATA,
	AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS,
	AGENT_EDIT_CHROME_DEFAULT_TELEPOINTER_ENABLED,
} from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { AgentEditChromeData } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { StreamingTelepointerIdentity } from '../../types/types';
import { TelepointerAvatar } from '../../ui/components/TelepointerAvatar/TelepointerAvatar';
import { aiSessionContextPluginKey } from '../ai-session-context/ai-session-context-plugin-key';
import { createStreamingTelepointerLabelResolver } from '../createStreamingTelepointerLabelResolver';
import { createTelepointerElement } from '../in-editor-direct-streaming/decorations';

import { removeAgentEditChrome } from './commands';
import { mapAgentEditChromeState } from './mapping';
import { agentEditChromePluginKey } from './plugin-key';
import { normalizeAgentEditChromeRanges } from './ranges';
import { initialAgentEditChromeState, reduceAgentEditChromeState } from './reducer';
import type { AgentEditChromeAction, AgentEditChromeState } from './types';

const LIST_PARENT_TYPES = new Set(['bulletList', 'orderedList', 'decisionList', 'taskList']);

/** Axe `list` requires every list child to be a list item. A telepointer widget on the list itself would sit beside those items, so place it inside the last item. */
function telepointerWidgetPos(doc: PMNode, to: number): number {
	const pos = Math.max(0, Math.min(to, doc.content.size));
	try {
		const $pos = doc.resolve(pos);
		if (LIST_PARENT_TYPES.has($pos.parent.type.name)) {
			return Math.max($pos.start(), pos - 1);
		}
	} catch {
		return pos;
	}
	return pos;
}

export const createAgentEditChromePlugin = ({
	api,
	getIntl,
	portalProviderAPI,
	streamingTelepointerIdentity,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
	portalProviderAPI?: PortalProviderAPI;
	streamingTelepointerIdentity?: StreamingTelepointerIdentity;
}): SafePlugin<AgentEditChromeState> => {
	const getTelepointerLabel = createStreamingTelepointerLabelResolver({
		formatMessage: getIntl().formatMessage,
		streamingTelepointerIdentity,
	});

	return new SafePlugin<AgentEditChromeState>({
		key: agentEditChromePluginKey,
		state: {
			init: () => initialAgentEditChromeState,
			apply: (tr, pluginState) => {
				// AI writes (stream update, restore, commit) replace a span that often
				// covers earlier tool-call nodes. Mapping through them collapses the
				// union onto the node being written — a merged flash, then one node.
				const isAiWrite =
					tr.getMeta('isAIRestoreTransaction') || tr.getMeta('isAIStreamingTransformation');
				const mappedState =
					tr.docChanged && !isAiWrite
						? mapAgentEditChromeState(pluginState, tr.mapping)
						: pluginState;
				const data = tr.getMeta(AGENT_EDIT_CHROME_DATA) as AgentEditChromeData | undefined;
				// Accumulate sequential tool-call SHOWs. A later producer that only
				// knows the current node must not wipe already-highlighted ones.
				const action: AgentEditChromeAction | undefined = data
					? {
							data: {
								...data,
								ranges: normalizeAgentEditChromeRanges(tr.doc, [
									...mappedState.ranges,
									...data.ranges,
								]),
							},
							type: 'SHOW',
						}
					: tr.getMeta(agentEditChromePluginKey);

				return action ? reduceAgentEditChromeState(mappedState, action) : mappedState;
			},
		},
		props: {
			decorations: (state) => {
				const pluginState = agentEditChromePluginKey.getState(state);
				const autoDismissAfterMs =
					pluginState?.autoDismissAfterMs ?? AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS;
				if (!pluginState?.ranges.length || autoDismissAfterMs <= 0) {
					return DecorationSet.empty;
				}

				const highlightStyle = [
					pluginState.highlightBackgroundColor &&
						`--agent-edit-chrome-background-color: ${pluginState.highlightBackgroundColor}`,
					pluginState.highlightBorderColor &&
						`--agent-edit-chrome-border-color: ${pluginState.highlightBorderColor}`,
				]
					.filter(Boolean)
					.join('; ');
				const decorations: Decoration[] = pluginState.ranges.map(({ from, to }) =>
					Decoration.inline(from, to, {
						class: 'ai-in-editor-ai-content',
						'data-testid': 'agent-edit-chrome-highlight',
						...(highlightStyle ? { style: highlightStyle } : {}),
					}),
				);
				if (pluginState.telepointerEnabled ?? AGENT_EDIT_CHROME_DEFAULT_TELEPOINTER_ENABLED) {
					const telepointerLabel =
						pluginState.telepointerLabel ?? getTelepointerLabel({ isAgentMentionEnabled: false });
					pluginState.ranges.forEach(({ to }, index) => {
						const { avatarContainer, element, labelElement } =
							createTelepointerElement(telepointerLabel);
						// Keys are deliberately independent of the revision. Producers restamp
						// while content streams in, and a revision-scoped key would make
						// ProseMirror discard the widget and remount the avatar portal on every
						// chunk. Keying on the range index lets the existing telepointer be
						// repositioned instead, so it walks forward without flicker.
						if (pluginState.agentIdentityAccountId) {
							const portalKey = `agentEditChromeTelepointerAvatar-${index}`;
							labelElement.style.display = 'none';
							avatarContainer.style.display = '';
							avatarContainer.dataset.agentEditChromeAvatarPortalKey = portalKey;
						}
						decorations.push(
							Decoration.widget(telepointerWidgetPos(state.doc, to), element, {
								key: `agent-edit-chrome-${index}`,
								side: 0,
							}),
						);
					});
				}

				return DecorationSet.create(state.doc, decorations);
			},
		},
		view: () => {
			let timeout: ReturnType<typeof setTimeout> | undefined;
			let revision = 0;
			const renderedAgentIdentityByPortalKey = new Map<string, string>();
			const removeStaleAvatarPortals = (activePortalKeys = new Set<string>()) => {
				renderedAgentIdentityByPortalKey.forEach((_identity, portalKey) => {
					if (!activePortalKeys.has(portalKey)) {
						portalProviderAPI?.remove(portalKey);
						renderedAgentIdentityByPortalKey.delete(portalKey);
					}
				});
			};
			return {
				update: (view) => {
					const pluginState = agentEditChromePluginKey.getState(view.state);
					const agentIdentityAccountId = pluginState?.agentIdentityAccountId;

					// Decorations create the portal containers; synchronize their React avatars after
					// those containers enter the editor DOM.
					const activePortalKeys = new Set<string>();
					if (agentIdentityAccountId && portalProviderAPI) {
						view.dom
							.querySelectorAll<HTMLElement>('[data-agent-edit-chrome-avatar-portal-key]')
							.forEach((container) => {
								const portalKey = container.dataset.agentEditChromeAvatarPortalKey;
								if (!portalKey) {
									return;
								}
								activePortalKeys.add(portalKey);
								if (renderedAgentIdentityByPortalKey.get(portalKey) !== agentIdentityAccountId) {
									portalProviderAPI.render(
										() =>
											React.createElement(TelepointerAvatar, {
												agentIdentityAccountId,
											}),
										container,
										portalKey,
									);
									renderedAgentIdentityByPortalKey.set(portalKey, agentIdentityAccountId);
								}
							});
					}
					removeStaleAvatarPortals(activePortalKeys);

					// Keep the highlight up across sequential tool calls in the same turn.
					// Auto-dismiss after a single apply would clear it before the next
					// commit restamps later nodes.
					const sessionStatus = aiSessionContextPluginKey.getState(view.state)?.status;
					const holdDismiss = sessionStatus === 'streaming' || sessionStatus === 'complete';
					if (holdDismiss) {
						if (timeout) {
							clearTimeout(timeout);
							timeout = undefined;
						}
						if (pluginState) {
							revision = pluginState.revision;
						}
						return;
					}

					// Session ended: dismiss the current treatment after the configured delay.
					if (!pluginState?.ranges.length) {
						return;
					}
					if (pluginState.revision === revision && timeout) {
						return;
					}
					revision = pluginState.revision;
					if (timeout) {
						clearTimeout(timeout);
					}
					const scheduledRevision = pluginState.revision;
					const autoDismissAfterMs =
						pluginState.autoDismissAfterMs ?? AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS;
					timeout = setTimeout(
						() => {
							api?.core?.actions.execute(removeAgentEditChrome(scheduledRevision));
						},
						Math.max(0, autoDismissAfterMs),
					);
				},
				destroy: () => {
					if (timeout) {
						clearTimeout(timeout);
					}
					removeStaleAvatarPortals();
				},
			};
		},
	});
};
